'use client'

import {ReactNode, useContext} from 'react'
import {Accordion, Button, Slideout, SmallText} from '@local/ui/components'
import {useTranslate} from '@local/hooks'
import {RetreatDetailsContext} from '@local/stores/contexts'

import CloseSVG from '@local/assets/svgs/icon-close.svg'
import GlobeSVG from '@local/assets/svgs/icon-globe.svg'
import DateSVG from '@local/assets/svgs/icon-date.svg'
import BedSVG from '@local/assets/svgs/icon-bed.svg'
import EyeSVG from '@local/assets/svgs/icon-eye.svg'
import PaymentSVG from '@local/assets/svgs/icon-payment.svg'
import {formatPrice} from '@local/utils/currency'

export const RetreatDetailsPanel = () => {
	const {isOpen, setIsOpen, product: productData} = useContext(RetreatDetailsContext)
	const onClickClose = () => { setIsOpen(false) }

	const locale = useTranslate()

	// const dates = productData.store.options.find(e => e.name === 'Date')?.values || [productData.datePeriod]
	const minPrice = productData?.store?.priceRange?.minVariantPrice || {amount: 0, currency: 'EUR'}
	const formattedPrice = formatPrice(minPrice?.amount, `en-NL`, minPrice?.currency, {minimumFractionDigits: 0, maximumFractionDigits: 0}).replace(/\s/g, '')
	const nextDates = productData?.nextDates?.map(e => e.period) || [productData.datePeriod]
	const nextDatesString = nextDates?.length > 1 ? nextDates.join('\n') : nextDates[0]
	return (
		<Slideout
			isOpen={isOpen}
			variant='right'
			onClickClose={onClickClose}
			className='bg-grey-4 text-white relative !px-0'>
			<div data-lenis-prevent className='overflow-y-scroll min-h-screen h-fit margin-px-1 pb-50 scrollbar-hide'>

				<div className='sticky top-0 z-1 flex items-center justify-between w-full py-10 lg:py-20 bg-grey-4'>
					<h2 className='w-fit text-18 uppercase'>{locale('Retreat Details')}</h2>
					<Button
						small
						variant='icon'
						onClick={onClickClose}
						className='px-16 aspect-square'>
						<CloseSVG />
					</Button>
				</div>

				<div className='flex flex-col gap-14 mt-20'>
					{productData.location && (
						<TitleTextIcon
							title={locale('Location')}
							text={productData?.location}>
							<GlobeSVG className='col-start-1 row-span-full w-16 h-16' />
						</TitleTextIcon>
					)}
					{!!nextDates.length && (
						<TitleTextIcon
							title={locale('Next Dates')}
							text={nextDatesString ?? 'Dates coming soon'}>
							<DateSVG className='col-start-1 row-span-full w-16 h-16' />
						</TitleTextIcon>
					)}
					{productData.specialGuests && (
						<TitleTextIcon
							title={locale('Hosted by')}
							text={productData?.specialGuests}>
							<EyeSVG className='col-start-1 row-span-full w-16 h-16' />
						</TitleTextIcon>
					)}
					{productData.accomodation && (
						<TitleTextIcon
							title={locale('Accommodation')}
							text={productData?.accomodation}>
							<BedSVG className='col-start-1 row-span-full w-16 h-16' />
						</TitleTextIcon>
					)}
					{formattedPrice && (
						<TitleTextIcon
							title={locale('Pricing starts at')}
							text={formattedPrice}>
							<PaymentSVG className='col-start-1 row-span-full w-16 h-16' />
						</TitleTextIcon>
					)}
				</div>

				<div className='mt-50'>
					<h3 className='text-18 tracking-tight uppercase mb-20'>{locale('Included')}</h3>
					<Accordion items={productData.includedItems} className='[&_p]:text-grey-1 mb-50' />
					<h3 className='text-18 tracking-tight uppercase mb-20'>{locale('Not Included')}</h3>
					<Accordion items={productData.notIncludedItems} className='[&_p]:text-grey-1' preExpanded={[]} />
					<h3 className='text-18 tracking-tight uppercase mt-50 mb-10 border-b border-grey-1/30 pb-20'>{locale('Overview')}</h3>
					<p className='text-11 uppercase tracking-wider border-b border-grey-1/30 pb-10'>Starting at {formattedPrice}</p>
					<p className='text-11 uppercase tracking-wider mt-10'>{productData?.overviewText || `${productData.daysOfExperience} days of experience`} </p>
				</div>

			</div>
		</Slideout>
	)
}

const TitleTextIcon = ({children, title, text}: { children: ReactNode, title: string, text: string }) => (
	<div className='grid grid-cols-[auto_1fr] justify-items-start items-start gap-y-5 gap-x-10'>
		{children}
		<h4 className='text-white text-11 mt-3 leading-none uppercase tracking-doubleWider col-start-2 row-start-1'>{title}:</h4>
		<SmallText textColor='grey-1' className='col-start-2 row-start-2 mt-0'>{text}</SmallText>
	</div>
)
